import * as THREE from 'three';
import { isDarkTheme } from '../theme.js';
import { createExtrudedGearGeometry } from './gearGeometry3d.js';
import { getSceneTheme3D } from './sceneTheme3d.js';

const BILLBOARD_COUNT_MIN = 24;

export class GearBackground3d {
  readonly group = new THREE.Group();
  private billboards: THREE.Mesh[] = [];
  private gearGeo: THREE.ExtrudeGeometry | null = null;

  build(boardExtent: number): void {
    this.clear();

    if (!isDarkTheme()) return;

    const theme = getSceneTheme3D('gear');
    this.gearGeo = createExtrudedGearGeometry();

    const count = Math.max(BILLBOARD_COUNT_MIN, boardExtent * 8);
    const spread = boardExtent * 2.5 + 4;

    for (let i = 0; i < count; i++) {
      const seed = i * 7919 + 13;
      const x = (((seed * 17) % 997) / 997 - 0.5) * spread * 2;
      const z = -6 - (((seed * 31) % 991) / 991) * (boardExtent + 6);
      const y = 0.5 + (((seed * 43) % 983) / 983 - 0.5) * spread;
      const scale = 0.12 + (seed % 20) / 100;
      const color = i % 3 === 0 ? theme.wallBrass : theme.wallSteel;
      const alpha = 0.1 + (seed % 12) / 100;

      const mat = new THREE.MeshStandardMaterial({
        color,
        roughness: 0.7,
        metalness: 0.5,
        transparent: true,
        opacity: alpha,
        emissive: 0x000000,
        emissiveIntensity: 0,
        depthWrite: false,
      });

      const mesh = new THREE.Mesh(this.gearGeo, mat);
      mesh.position.set(x, y, z);
      mesh.userData.baseX = x;
      mesh.userData.baseY = y;
      mesh.scale.setScalar(scale);
      mesh.rotation.y = (seed % 360) * (Math.PI / 180);
      this.group.add(mesh);
      this.billboards.push(mesh);
    }
  }

  faceCamera(camera: THREE.Camera, now = performance.now()): void {
    const t = now / 1000;
    for (let i = 0; i < this.billboards.length; i++) {
      const mesh = this.billboards[i]!;
      const seed = i * 7919 + 13;
      const depth = 0.35 + (seed % 7) / 10;
      const baseX = mesh.userData.baseX as number;
      const baseY = mesh.userData.baseY as number;
      mesh.position.x = baseX + Math.sin(t * 0.08 + seed * 0.01) * 0.18 * depth;
      mesh.position.y = baseY + Math.cos(t * 0.06 + seed * 0.013) * 0.14 * depth;
      mesh.lookAt(camera.position.x, mesh.position.y, camera.position.z);
    }
  }

  private clear(): void {
    for (const mesh of this.billboards) {
      (mesh.material as THREE.Material).dispose();
      this.group.remove(mesh);
    }
    this.billboards = [];
    this.gearGeo = null;
  }

  dispose(): void {
    this.clear();
  }
}
